import { Trans, useLingui } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import { ShipmentCarrierService, ShipmentHandover } from '@/models/shipment';
import useViewPort from '@/utils/useViewport';

import { ArticleSelect } from './components/ArticleSelect';
import { ShipmentFormArticle } from './components/ArticleSelect/ArticleSelect';
import { CarrierSelect } from './components/CarrierSelect';
import { PlaceSelect } from './components/PlaceSelect';
import { ShipmentFormPlace } from './components/PlaceSelect/PlaceSelect';

const ShipmentForm = ({
  origin,
  setOrigin,
  destination,
  setDestination,
  articles,
  setArticles,
  carrierService,
  setCarrierService,
  handover,
  setHandover,
  pickupDate,
  setPickupDate,
  trackingId,
  setTrackingId,
  error,
}: {
  origin?: ShipmentFormPlace;
  setOrigin: (address?: ShipmentFormPlace) => void;
  destination?: ShipmentFormPlace;
  setDestination: (address?: ShipmentFormPlace) => void;
  articles: ShipmentFormArticle[];
  setArticles: (articles: ShipmentFormArticle[]) => void;
  carrierService?: ShipmentCarrierService;
  setCarrierService: (carrier: ShipmentCarrierService) => void;
  handover?: ShipmentHandover;
  setHandover: (handover?: ShipmentHandover) => void;
  pickupDate?: string;
  setPickupDate: (pickupDate?: string) => void;
  trackingId?: string;
  setTrackingId: (trackingId?: string) => void;
  error?: string;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  return (
    <Stack gap={isMobile ? '1rem' : '1.5rem'}>
      <Stack row mobileColumn gap={isMobile ? '1rem' : '1.5rem'}>
        <PlaceSelect
          type="origin"
          label={t({ id: 'shipments.new.origin.label', message: 'Origin' })}
          placeholder={t({ id: 'shipments.new.origin.placeholder', message: 'Select an origin' })}
          selected={origin}
          setSelected={setOrigin}
          isDisabled={articles.length > 0}
        />
        <PlaceSelect
          type="destination"
          label={t({ id: 'shipments.new.destination.label', message: 'Destination' })}
          placeholder={t({
            id: 'shipments.new.destination.placeholder',
            message: 'Select a destination',
          })}
          carrierService={carrierService}
          selected={destination}
          setSelected={setDestination}
          isDisabled={articles.length > 0}
        />
      </Stack>
      <ArticleSelect
        label={
          <div className="label-100-medium">
            <Trans id="shipments.new.article.label">Items</Trans>
          </div>
        }
        ariaLabel={t({ id: 'shipments.new.article.label', message: 'Items' })}
        addedArticles={articles}
        setAddedArticles={setArticles}
        origin={origin}
        setOrigin={setOrigin}
        destination={destination}
        setDestination={setDestination}
      />

      <CarrierSelect
        articles={articles}
        origin={origin}
        destination={destination}
        carrierService={carrierService}
        setCarrierService={setCarrierService}
        handover={handover}
        setHandover={setHandover}
        pickupDate={pickupDate}
        setPickupDate={setPickupDate}
        trackingId={trackingId}
        setTrackingId={setTrackingId}
      />
      {error && (
        <div>
          <Message type="error">{error}</Message>
        </div>
      )}
    </Stack>
  );
};

export default ShipmentForm;
